import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useApi } from "../context/ApiContext";
import { FaChevronRight } from "react-icons/fa";
import { useTranslatedValue } from "../hooks/useTranslatedValue";

interface Category {
 _id: string;
 name: string;
 slug: string;
 children?: Category[];
 translations?: {
  [fieldName: string]: {
   [languageCode: string]: string;
  };
 };
}

// Component to render category name with translation
const CategoryName: React.FC<{ category: Category }> = ({ category }) => {
 const translatedName = useTranslatedValue(category, "name");
 return <>{translatedName}</>;
};

interface CategoryFilterProps {
 selectedCategory?: string;
 onCategoryChange: (categoryId: string | null) => void;
 variant?: "horizontal" | "vertical";
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
 selectedCategory,
 onCategoryChange,
 variant = "horizontal",
}) => {
 const { api } = useApi();
 const [allCategories, setAllCategories] = useState<Category[]>([]);
 const [loading, setLoading] = useState(true);

 const fetchCategories = useCallback(async () => {
  try {
   setLoading(true);
   const response = await api.get("/categories");
   setAllCategories(response.data.categories || []);
  } catch (error) {
   console.error("Error fetching categories:", error);
  } finally {
   setLoading(false);
  }
 }, [api]);

 useEffect(() => {
  fetchCategories();
 }, [fetchCategories]);

 // Get root categories and selected category's children
 const displayCategories = useMemo(() => {
  // Helper function to find category by ID or slug
  const findCategoryById = (cats: Category[], id: string): Category | null => {
   for (const cat of cats) {
    if (cat._id === id || cat.slug === id) {
     return cat;
    }
    if (cat.children) {
     const found = findCategoryById(cat.children, id);
     if (found) return found;
    }
   }
   return null;
  };

  // Helper function to find parent category that contains the given category
  const findParentCategory = (cats: Category[], targetId: string): Category | null => {
   for (const cat of cats) {
    if (cat.children) {
     const found = cat.children.find((child) => child._id === targetId || child.slug === targetId);
     if (found) {
      return cat;
     }
     const parent = findParentCategory(cat.children, targetId);
     if (parent) return parent;
    }
   }
   return null;
  };

  const result: (Category & { isChild?: boolean })[] = [];

  // Always show all root categories first
  allCategories.forEach((cat) => {
   result.push({
    _id: cat._id,
    name: cat.name,
    slug: cat.slug,
   });
  });

  // If a category is selected, show its children after all root categories
  if (selectedCategory) {
   const selected = findCategoryById(allCategories, selectedCategory);
   if (selected) {
    // Check if selected is a root category
    const isRoot = allCategories.some((cat) => cat._id === selected._id || cat.slug === selected.slug);
    
    if (isRoot && selected.children && selected.children.length > 0) {
     // Selected is a root category with children, show separator and children
     result.push({ _id: "separator", name: "", slug: "", isChild: true } as Category & { isChild: boolean });
     selected.children.forEach((child) => {
      result.push({ ...child, isChild: true });
     });
    } else if (!isRoot) {
     // Selected is a child category, find its parent and show all siblings
     const parent = findParentCategory(allCategories, selectedCategory);
     if (parent && parent.children && parent.children.length > 0) {
      result.push({ _id: "separator", name: "", slug: "", isChild: true } as Category & { isChild: boolean });
      parent.children.forEach((child) => {
       result.push({ ...child, isChild: true });
      });
     }
    }
   }
  }

  return result;
 }, [allCategories, selectedCategory]);

 if (loading) {
  return (
   <div className="flex items-center gap-2">
    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded"></div>
    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded"></div>
    <div className="h-8 w-24 bg-gray-200 animate-pulse rounded"></div>
   </div>
  );
 }

 if (variant === "horizontal") {
  return (
   <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
    <button
     onClick={() => onCategoryChange(null)}
     className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-all ${
      !selectedCategory
       ? "bg-store-primary text-white"
       : "bg-white text-store-primary border border-store-accent-light hover:border-store-secondary"
     }`}
    >
     All Products
    </button>
    {displayCategories.map((category, _index) => {
     // Handle separator
     if (category._id === "separator") {
      return (
       <div key="separator" className="flex items-center px-2">
        <FaChevronRight className="text-store-accent text-xs" />
       </div>
      );
     }

     const isSelected = selectedCategory === category.slug || selectedCategory === category._id;
     return (
      <button
       key={category._id}
       onClick={() => onCategoryChange(category.slug || category._id)}
       className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-all ${
        isSelected
         ? "bg-store-primary text-white"
         : category.isChild
         ? "bg-store-background text-store-primary border border-store-accent-light hover:border-store-secondary"
         : "bg-white text-store-primary border border-store-accent-light hover:border-store-secondary"
       }`}
      >
       <CategoryName category={category} />
      </button>
     );
    })}
   </div>
  );
 }

 // Vertical variant (for sidebar if needed)
 return (
  <div className="space-y-1">
   <button
    onClick={() => onCategoryChange(null)}
    className={`w-full text-left px-3 py-2 rounded text-sm ${
     !selectedCategory
      ? "bg-store-primary text-white"
      : "text-store-primary hover:bg-store-background"
    }`}
   >
    All Categories
   </button>
   {displayCategories.map((category) => {
    // Handle separator
    if (category._id === "separator") {
     return (
      <div key="separator" className="flex items-center px-3 py-2">
       <FaChevronRight className="text-store-accent text-xs" />
      </div>
     );
    }

    const isSelected = selectedCategory === category.slug || selectedCategory === category._id;
    return (
     <button
      key={category._id}
      onClick={() => onCategoryChange(category.slug || category._id)}
      className={`w-full text-left px-3 py-2 rounded text-sm ${
       isSelected
        ? "bg-store-primary text-white"
        : category.isChild
        ? "text-store-primary hover:bg-store-background pl-6"
        : "text-store-primary hover:bg-store-background"
      }`}
     >
       <CategoryName category={category} />
     </button>
    );
   })}
  </div>
 );
};
